import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, Calendar, Trophy, Sparkles, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { CreditCardVisual } from '@/components/cards/CreditCardVisual';
import { TransactionRow } from '@/components/transactions/TransactionRow';
import { Cards as CardsApi, Transactions } from '@/lib/api';
import { formatINR } from '@/lib/format';

export default function CardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const lib = await CardsApi.library();
        const c = lib.find((x) => x.id === id);
        setCard(c || lib[0] || null);
        const t = await Transactions.list({ card_id: id, limit: 50 }).catch(() => []);
        setTxns(t || []);
      } finally { setLoading(false); }
    };
    run();
  }, [id]);

  if (loading || !card) {
    return <div className="min-h-[40vh] grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const ytdSpend = txns.reduce((s, t) => s + (t.amount || 0), 0);
  const ytdRewards = txns.reduce((s, t) => s + (t.reward_value_inr || 0), 0);

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/cards')} className="gap-1.5 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back to cards
      </Button>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-2"><CreditCardVisual card={card} size="lg" /></div>
        <div className="lg:col-span-3 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-accent">{card.bank}</div>
            <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">{card.name}</h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{card.tier}</Badge>
              <Badge variant="outline" className="font-mono-num">{card.currency}</Badge>
              <Badge variant="outline">{card.network}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="p-4"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">YTD Spend</div><div className="mt-1 font-display text-lg font-semibold">{formatINR(ytdSpend, { compact: true })}</div></Card>
            <Card className="p-4"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">YTD Rewards</div><div className="mt-1 font-display text-lg font-semibold text-emerald-600 dark:text-emerald-400">{formatINR(ytdRewards, { compact: true })}</div></Card>
            <Card className="p-4"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Annual fee</div><div className="mt-1 font-display text-lg font-semibold">{formatINR(card.annual_fee)}</div></Card>
            <Card className="p-4"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pt value</div><div className="mt-1 font-display text-lg font-semibold">₹{(card.point_value_inr || 1).toFixed(2)}</div></Card>
          </div>
        </div>
      </div>

      <Tabs defaultValue="rewards">
        <TabsList>
          <TabsTrigger value="rewards">Reward DSL</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
          <TabsTrigger value="benefits">Fees & benefits</TabsTrigger>
          <TabsTrigger value="txns">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="rewards" className="mt-4">
          <Card className="p-5">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /><h3 className="font-display text-base font-semibold">Reward rules ({(card.rules || []).length})</h3></div>
            <div className="mt-4 divide-y divide-border">
              {(card.rules || []).map((r) => {
                const cats = (r.match?.categories || []).join(', ') || 'General';
                const mccs = (r.match?.mcc || []).slice(0, 5).join(', ');
                return (
                  <div key={r.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">{r.label}{r.exclude && <Badge variant="outline" className="text-rose-500 border-rose-500/30 bg-rose-500/5">Excluded</Badge>}{r.cap?.amount_inr && <Badge variant="outline">Cap ₹{Number(r.cap.amount_inr).toLocaleString('en-IN')}</Badge>}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Categories: {cats}{mccs && ` · MCC: ${mccs}`}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {r.earn ? (
                        <Badge className="bg-primary/10 text-primary border-primary/20">
                          {r.earn.type === 'percent_cashback' ? `${r.earn.rate}%` : `${r.earn.rate}x`}
                        </Badge>
                      ) : (
                        <Badge variant="outline">—</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="milestones" className="mt-4">
          {(card.milestones || []).length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">No milestones on this card.</Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {card.milestones.map((m) => {
                const current = (card.milestone_progress || {})[m.id] || 0;
                const pct = Math.min(100, Math.round((current / m.target_inr) * 100));
                return (
                  <Card key={m.id} className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-display text-base font-semibold">{m.name}</div>
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Trophy className="h-3.5 w-3.5 text-accent" />{m.reward}</div>
                      </div>
                      <Badge className="font-mono-num" variant="secondary">{pct}%</Badge>
                    </div>
                    <Progress value={pct} className="h-1.5 mt-4" />
                    <div className="mt-2 text-xs text-muted-foreground font-mono-num">{formatINR(current, { compact: true })} / {formatINR(m.target_inr, { compact: true })}</div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="benefits" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" /><h3 className="font-display text-base font-semibold">Fees</h3></div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between"><span>Annual fee</span><span className="font-mono-num font-medium">{formatINR(card.annual_fee)}</span></div>
                <div className="flex items-center justify-between"><span>Fee waiver</span><span className="font-mono-num font-medium">{card.fee_waiver_inr ? formatINR(card.fee_waiver_inr) : 'No'}</span></div>
                <div className="flex items-center justify-between"><span>Welcome bonus</span><span className="font-medium">{card.welcome_bonus || '—'}</span></div>
              </div>
            </Card>
            <Card className="p-5">
              <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-accent" /><h3 className="font-display text-base font-semibold">Benefits</h3></div>
              <ul className="mt-3 space-y-2">{(card.benefits || []).map((b) => <li key={b} className="flex items-center gap-2 text-sm"><Calendar className="h-3.5 w-3.5 text-primary" /> {b}</li>)}</ul>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="txns" className="mt-4">
          <Card className="p-3">
            {txns.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No transactions yet on this card.</div>
            ) : (
              txns.map((t) => <TransactionRow key={t.id} tx={{ ...t, rewardValue: t.reward_value_inr, rewardCurrency: t.reward_currency, cardName: t.card_name, missed: t.missed_inr }} />)
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
